import { get, create, updatePatch } from "./apiService";

export type DposPaymentMethod = "88pay" | "crypto" | "paylink";
export type DposCryptoFlow = "internal_wallet" | "external_wallet";
export type DposSessionStatus =
  | "active"
  | "pending"
  | "processing"
  | "paid"
  | "confirmed"
  | "expired"
  | "cancelled"
  | "failed";

export interface UpdateAcceptedCurrencyRequest {
  assetId: string;
  isAccepted: boolean;
  createWalletIfMissing?: boolean;
}

export interface DposAcceptedCurrencyBusinessWallet {
  exists: boolean;
  address: string | null;
  fireblocksId: string | null;
  network: string | null;
  identifierEmail: string | null;
}

export interface DposCashierModeSession {
  id?: string;
  _id?: string;
  businessId?: string;
  userId?: string;
  status?: "active" | "exited" | "expired" | string;
  expiresAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface DposCashierModeStatus {
  hasPin: boolean;
  pinLength: number | null;
  lockedUntil: string | null;
  isLocked: boolean;
  session: DposCashierModeSession | null;
}

export interface SetupDposCashierPinRequest {
  pin: string;
  confirmPin?: string;
}

export interface VerifyDposCashierPinRequest {
  pin: string;
}

export interface ExitDposCashierModeRequest {
  pin?: string;
}

export interface DposAcceptedCurrency {
  assetId: string;
  symbol: string;
  assetName: string;
  image: string | null;
  chain: string | null;
  isAccepted: boolean;
  businessWallet: DposAcceptedCurrencyBusinessWallet;
}

export interface CreateDposSessionRequest {
  amount: number;
  currency?: string;
  paymentMethod?: DposPaymentMethod;
  cryptoFlow?: DposCryptoFlow;
  cryptoAsset?: string;
}

export interface DposSessionDetails {
  id?: string;
  _id?: string;
  amount?: number | string;
  asset?: string;
  currency?: string;
  status?: DposSessionStatus | string;
  payId?: string;
  qrCode?: string;
  qrPayload?: string;
  qrCodeValue?: string;
  deepLink?: string;
  deeplink?: string;
  payLinkId?: string;
  paymentLink?: string;
  externalWalletQr?: string;
  walletAddress?: string;
  address?: string;
  reference?: string;
  paymentReference?: string;
  merchantReference?: string;
  cryptoAmount?: number | string;
  cryptoCurrency?: string;
  cryptoAsset?: string;
  assetSymbol?: string;
  network?: string;
  chainName?: string;
  qrCodeExpiresAt?: string;
  expiresAt?: string;
  paidAt?: string | null;
  createdAt?: string;
  paymentMethod?: DposPaymentMethod | string;
  paymentOption?: string;
  flowType?: string;
  errorMessage?: string;
  metadata?: Record<string, unknown>;
}

export interface DposSessionReceipt {
  paymentId: string;
  status: DposSessionStatus | string;
  amount: string;
  currency: string;
  paymentMethod: DposPaymentMethod | string;
  cryptoFlow?: DposCryptoFlow | string | null;
  paidWith: string | null;
  assetUsed: string | null;
  fiatEquivalent: string | null;
  reference: string | null;
  networkFee: string | null;
  totalCost: string | null;
  totalCostFiatEquivalent: string | null;
  timestamp: string | null;
}

export interface DposSessionListResponse {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  limit: number;
  results: DposSessionDetails[];
}

interface DposServiceErrorResponse {
  message?: string;
  error?: string;
  data?: Record<string, unknown>;
}

export interface DposRequestError extends Error {
  statusCode?: number;
  data?: Record<string, unknown>;
}

interface DposServiceError {
  message?: string;
  response?: {
    status?: number;
    data?: DposServiceErrorResponse;
  };
}

function getDposServiceErrorMessage(error: unknown, fallback: string): string {
  if (typeof error !== "object" || error === null) {
    return fallback;
  }

  const serviceError = error as DposServiceError;

  return (
    serviceError.response?.data?.message ||
    serviceError.response?.data?.error ||
    serviceError.message ||
    fallback
  );
}

function buildDposServiceError(
  error: unknown,
  fallback: string
): DposRequestError {
  const serviceError = (error || {}) as DposServiceError;
  const nextError = new Error(
    getDposServiceErrorMessage(error, fallback)
  ) as DposRequestError;

  nextError.statusCode = serviceError.response?.status;
  nextError.data = serviceError.response?.data?.data;

  return nextError;
}

export const getAcceptedCurrencies = async (businessId: string) => {
  try {
    const response = await get(
      `/api/v2/business/${businessId}/wallet/dpos/accepted-currencies`
    );
    return response;
  } catch (error: unknown) {
    throw buildDposServiceError(
      error,
      `Could not get accepted currencies: ${getDposServiceErrorMessage(
        error,
        "Unknown error"
      )}`
    );
  }
};

export const updateAcceptedCurrency = async (
  businessId: string,
  data: UpdateAcceptedCurrencyRequest
) => {
  try {
    const response = await updatePatch(
      `/api/v2/business/${businessId}/wallet/dpos/accepted-currencies`,
      {},
      data
    );
    return response;
  } catch (error: unknown) {
    throw buildDposServiceError(
      error,
      `Could not update accepted currency: ${getDposServiceErrorMessage(
        error,
        "Unknown error"
      )}`
    );
  }
};

export const createDposSession = async (
  businessId: string,
  data: CreateDposSessionRequest
) => {
  try {
    const response = await create(
      `/api/v2/business/${businessId}/wallet/dpos/sessions`,
      data
    );
    return response;
  } catch (error: unknown) {
    throw new Error(
      `Could not create DPOS session: ${getDposServiceErrorMessage(
        error,
        "Unknown error"
      )}`
    );
  }
};

export const getDposSessionQr = async (
  businessId: string,
  sessionId: string
) => {
  try {
    const response = await get(
      `/api/v2/business/${businessId}/wallet/dpos/sessions/${sessionId}/qr`
    );
    return response;
  } catch (error: unknown) {
    throw new Error(
      `Could not get DPOS session QR: ${getDposServiceErrorMessage(
        error,
        "Unknown error"
      )}`
    );
  }
};

export const listDposSessions = async (
  businessId: string,
  params: {
    page?: number;
    limit?: number;
    status?: string;
    sort?: string;
    search?: string;
  } = {}
) => {
  try {
    const query = new URLSearchParams();
    if (params.page) query.set("page", String(params.page));
    if (params.limit) query.set("limit", String(params.limit));
    if (params.status) query.set("status", params.status);
    if (params.sort) query.set("sort", params.sort);
    if (params.search) query.set("search", params.search);
    const qs = query.toString();
    const response = await get(
      `/api/v2/business/${businessId}/wallet/dpos/sessions${qs ? `?${qs}` : ""}`
    );
    return response;
  } catch (error: unknown) {
    throw new Error(
      `Could not list DPOS sessions: ${getDposServiceErrorMessage(
        error,
        "Unknown error"
      )}`
    );
  }
};

export const getDposSessionReceipt = async (
  businessId: string,
  sessionId: string
) => {
  try {
    const response = await get(
      `/api/v2/business/${businessId}/wallet/dpos/sessions/${sessionId}/receipt`
    );
    return response;
  } catch (error: unknown) {
    throw new Error(
      `Could not get DPOS session receipt: ${getDposServiceErrorMessage(
        error,
        "Unknown error"
      )}`
    );
  }
};

export const getDposPerformance = async (
  businessId: string,
  period: string = "1w"
) => {
  try {
    const response = await get(
      `/api/v2/business/${businessId}/wallet/dpos/performance?period=${period}`
    );
    return response;
  } catch (error: unknown) {
    throw new Error(
      `Could not get DPOS performance: ${getDposServiceErrorMessage(
        error,
        "Unknown error"
      )}`
    );
  }
};

export const cancelDposSession = async (
  businessId: string,
  sessionId: string
) => {
  try {
    const response = await create(
      `/api/v2/business/${businessId}/wallet/dpos/sessions/${sessionId}/cancel`,
      {}
    );
    return response;
  } catch (error: unknown) {
    throw new Error(
      `Could not cancel DPOS session: ${getDposServiceErrorMessage(
        error,
        "Unknown error"
      )}`
    );
  }
};

export const getDposCashierModeStatus = async (businessId: string) => {
  try {
    const response = await get(
      `/api/v2/business/${businessId}/wallet/dpos/cashier-mode`
    );
    return response;
  } catch (error: unknown) {
    throw new Error(
      `Could not get DPOS cashier mode status: ${getDposServiceErrorMessage(
        error,
        "Unknown error"
      )}`
    );
  }
};

export const setupDposCashierPin = async (
  businessId: string,
  data: SetupDposCashierPinRequest
) => {
  try {
    const response = await create(
      `/api/v2/business/${businessId}/wallet/dpos/cashier-mode/pin`,
      data
    );
    return response;
  } catch (error: unknown) {
    throw new Error(
      `Could not set up DPOS cashier PIN: ${getDposServiceErrorMessage(
        error,
        "Unknown error"
      )}`
    );
  }
};

export const verifyDposCashierPin = async (
  businessId: string,
  data: VerifyDposCashierPinRequest
) => {
  try {
    const response = await create(
      `/api/v2/business/${businessId}/wallet/dpos/cashier-mode/verify`,
      data
    );
    return response;
  } catch (error: unknown) {
    throw new Error(
      `Could not verify DPOS cashier PIN: ${getDposServiceErrorMessage(
        error,
        "Unknown error"
      )}`
    );
  }
};

export const exitDposCashierMode = async (
  businessId: string,
  data: ExitDposCashierModeRequest = {}
) => {
  try {
    const response = await create(
      `/api/v2/business/${businessId}/wallet/dpos/cashier-mode/exit`,
      data
    );
    return response;
  } catch (error: unknown) {
    throw new Error(
      `Could not exit DPOS cashier mode: ${getDposServiceErrorMessage(
        error,
        "Unknown error"
      )}`
    );
  }
};
